import { PaginationQueryParams } from '@/api/types';
import VehiclesTable from '@/components/tables/VehiclesTable';
import useDebounce from '@/utils/hooks/useDebounce';
import { ControlPoint, GridView, Search, ViewHeadline } from '@mui/icons-material';
import { Button, Grid, IconButton, InputAdornment } from '@mui/material';
import { Box } from '@mui/system';
import {
  useOperatorsVehiclesFilterQuery,
  usePostOperatorsVehiclesActivationMutation,
} from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import ComposedDrawerMenuAppBar from 'components/ComposedDrawerMenuAppBar';
import VehicleCreateModalForm from 'components/forms/FleetFormFactory/VehicleCreateModalForm';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ControlledField, PageLoaderFullScreen, UserPermission, VehicleCard } from 'remote/global';
import { ROUTES } from 'utils/data/routes';
import { hasWritePermission } from 'utils/helpers';

function FleetView() {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const [pageNo, setPageNo] = useState(0);
  const [viewMode, setViewMode] = useState('card');
  const pageSize = viewMode === 'table' ? 20 : 100;

  const { control, watch } = useForm<PaginationQueryParams>({
    defaultValues: { search: '' },
  });
  const [search] = watch(['search']);
  const debouncedSearch = useDebounce(search, 300);

  const queryParams = useMemo(
    () => ({
      pageNo: String(pageNo),
      pageSize: String(pageSize),
      search: debouncedSearch,
    }),
    [pageNo, pageSize, debouncedSearch]
  );

  const { data: vehicles, isLoading, isSuccess } = useOperatorsVehiclesFilterQuery(queryParams);

  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const navigate = useNavigate();
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { permissions = {} } = userInfo ?? {};
  const isViewVehicleEnabled = ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
    permissions?.VEHICLE_DETAILS
  );
  const postOperatorsVehiclesActivationMutation = usePostOperatorsVehiclesActivationMutation();

  const handleActivationChange = async (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    await postOperatorsVehiclesActivationMutation.mutateAsync({
      availableForOffer: event.target.checked,
      vehicleIds: [id],
    });
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  if (isLoading) return <PageLoaderFullScreen />;
  if (!isSuccess) return null;

  return (
    <>
      <VehicleCreateModalForm
        isModalOpen={isCreateVehicleModalOpen}
        setIsModalOpen={setIsCreateVehicleModalOpen}
      />
      <ComposedDrawerMenuAppBar
        title={t('FleetView.title')}
        slot={
          <Button
            size='small'
            variant='text'
            onClick={() => setIsCreateVehicleModalOpen(true)}
            startIcon={<ControlPoint />}
            data-testid='add-vehicle'
            disabled={!hasWritePermission(permissions?.VEHICLE_DETAILS)}
            sx={{
              minWidth: 46,
              py: 0,
              height: 'auto',
              '.MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
              textDecoration: 'none',
            }}
          >
            <Box component='p' sx={{ display: { xs: 'none', sm: 'unset' }, m: 0 }}>
              {t('FleetView.actions.createVehicle')}
            </Box>
          </Button>
        }
      />

      <Grid container alignItems='end' justifyContent='space-between' sx={{ p: 2, pb: 0 }}>
        <Grid item sx={{ width: '295px' }}>
          <ControlledField
            control={control}
            name='search'
            fieldProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              },
              fullWidth: true,
            }}
          />
        </Grid>
        <Grid item>
          <Box>
            <IconButton
              onClick={() => setViewMode('table')}
              sx={{ p: 0, color: viewMode === 'table' ? 'primary.main' : undefined }}
            >
              <ViewHeadline />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('card')}
              sx={{ p: 0, color: viewMode === 'card' ? 'primary.main' : undefined }}
            >
              <GridView />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {viewMode === 'table' && vehicles ? (
        <VehiclesTable
          vehicles={vehicles}
          loading={isLoading}
          onActivationChange={(id, checked) =>
            handleActivationChange(
              { target: { checked } } as React.ChangeEvent<HTMLInputElement>,
              Number(id)
            )
          }
          onPageChange={handlePageChange}
          currentPage={pageNo}
          pageSize={pageSize}
          totalElements={vehicles.totalElements}
        />
      ) : (
        <Grid item container spacing={3} sx={{ p: { xs: 1, md: 2 } }}>
          {vehicles?.elements.map((bus) => (
            <Grid item key={bus.id} data-testid={bus.licensePlate}>
              <VehicleCard
                bus={bus}
                location={bus.currentLocation?.address || bus.defaultLocation?.address || ''}
                switchChecked={bus.availableForOffer}
                onActivationChange={(event) => handleActivationChange(event, bus.id)}
                {...(isViewVehicleEnabled && {
                  onClick: () =>
                    navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, bus.id].join('/')),
                })}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

export default FleetView;
